export interface ITypedLocalStorageService<T extends TypedLocalStorageObject = Record<string, any>> {
  get(key: string & keyof T): string | null;
  getJSON<K extends string & keyof T>(key: K): T[K] | null;
  isLocalStorageAvailable(): boolean;
  remove(key: string & keyof T): void;
  set(key: string & keyof T, value: string): void;
  setJSON<K extends string & keyof T>(key: K, value: T[K]): void;
  setStrict(key: string & keyof T, value: string): void;
}

export type TypedLocalStorageObject = Record<string, any>;

export interface LocalStorageConfig {
  keyPrefix: string; // Key prefix to avoid collisions.
}

/** Default configuration for TypedLocalStorage operations. */
export const LOCAL_STORAGE_DEFAULT_CONFIG: LocalStorageConfig = {
  keyPrefix: ''
};

/**
 * SSR-safe localStorage wrapper with optional schema typing.
 * All operations silently return null/void on failure (SSR, private mode, quota exceeded, etc.).
 * Untyped (default): accepts any string key, no prefix.
 * Typed: pass a schema generic to restrict keys and type return values.
 * @example
 * ```ts
 * // Untyped — general-purpose access
 * const storage = new TypedLocalStorage();
 * storage.set('my-key', 'value');
 * storage.get('my-key'); // string | null
 * // Typed — schema-restricted keys with JSON serialization
 * const typed = new TypedLocalStorage<{ authToken: AuthData }>({ keyPrefix: 'app_' });
 * typed.setJSON('authToken', data); // key restricted, value typed
 * typed.getJSON('authToken');       // AuthData | null
 * ```
 */
export class TypedLocalStorage<T extends TypedLocalStorageObject = Record<string, any>>
  implements ITypedLocalStorageService<T>
{
  private config: LocalStorageConfig;

  /**
   * Initializes the TypedLocalStorage service with optional configuration.
   * @param config - Partial configuration to override defaults.
   */
  constructor(config: Partial<LocalStorageConfig> = {}) {
    this.config = { ...LOCAL_STORAGE_DEFAULT_CONFIG, ...config };
  }

  /**
   * Reads a raw string value from localStorage.
   * Returns null if unavailable, missing, or on error.
   * @param key - The key to retrieve.
   */
  get(key: string & keyof T): string | null {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(this._getPrefixedKey(key));
    } catch {
      return null;
    }
  }

  /**
   * Reads and JSON-parses a value from localStorage.
   * Returns null if unavailable, missing, or unparseable.
   * @param key - The key to retrieve and parse.
   */
  getJSON<K extends string & keyof T>(key: K): T[K] | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(this._getPrefixedKey(key));
      if (raw === null) return null;
      return JSON.parse(raw) as T[K];
    } catch {
      return null;
    }
  }

  /**
   * Checks if localStorage is available and functional.
   */
  public isLocalStorageAvailable(): boolean {
    if (typeof window === 'undefined') return false;
    try {
      const testKey = '__localStorage_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Removes a key from localStorage. Silently ignores errors.
   * @param key - The key to remove.
   */
  remove(key: string & keyof T): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(this._getPrefixedKey(key));
    } catch {
      /** Silently ignore */
    }
  }

  /**
   * Writes a string value to localStorage. Silently ignores errors.
   * @param key - The key to write.
   * @param value - The string value to store.
   */
  set(key: string & keyof T, value: string): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(this._getPrefixedKey(key), value);
    } catch {
      /** Silently ignore — handles private mode, quota exceeded, etc. */
    }
  }

  /**
   * JSON-stringifies and writes a value to localStorage. Silently ignores errors.
   * @param key - The key to write.
   * @param value - The value to JSON-stringify and store.
   */
  setJSON<K extends string & keyof T>(key: K, value: T[K]): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(this._getPrefixedKey(key), JSON.stringify(value));
    } catch {
      /** Silently ignore */
    }
  }

  /**
   * Writes a string value to localStorage, propagating any error (eg: `QuotaExceededError`).
   * Use this when the caller needs to react to a failed write — most callers should use `set` instead.
   * SSR is still short-circuited to a no-op.
   * @param key - The key to write.
   * @param value - The string value to store.
   */
  setStrict(key: string & keyof T, value: string): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(this._getPrefixedKey(key), value);
  }

  /**
   * Generates a prefixed key to avoid collisions in localStorage.
   * Eg: If the key is "userSettings" and the prefix is "app_", the resulting key will be "app_userSettings".
   * @param key - The original key (non-prefixed).
   */
  private _getPrefixedKey(key: string): string {
    return `${this.config.keyPrefix}${key}`;
  }
}
